import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { SITE } from "../data/site";
import { SERVICES } from "../data/services";
import { PRIORITY_MARKETS, ALL_MARKERS } from "../data/locations";
import { Wordmark } from "./Navbar";
import { track } from "../lib/analytics";

// Company column — explicit list (primary nav no longer carries Home/Careers;
// the logo is the Home link and Careers lives here in the footer).
const COMPANY_NAV = [
  { label: "About", to: "/about" },
  { label: "Industries", to: "/industries" },
  { label: "Our Work", to: "/our-work" },
  { label: "Clients", to: "/clients" },
  { label: "Locations", to: "/locations" },
  { label: "Insights", to: "/insights" },
  { label: "Careers", to: "/careers" },
];

// Equal-height column header so all four columns start on the same baseline.
function ColHead({ children }) {
  return (
    <div className="flex h-11 items-center">
      <p className="eyebrow-dark">{children}</p>
    </div>
  );
}

// Structured four-column footer: Brand / Services / Company / Contact,
// priority-market strip, then a bottom legal row with copyright.
export function Footer() {
  const phone = SITE.contact.phone.replace(/\s/g, "");
  const wa = SITE.contact.whatsapp.replace(/\s/g, "").replace("+", "");
  const markets = ALL_MARKERS.filter((m) => PRIORITY_MARKETS.includes(m.id));
  const socials = [
    SITE.social.linkedin && { label: "LinkedIn", href: SITE.social.linkedin, testid: "footer-linkedin-link" },
    SITE.social.instagram && { label: "Instagram", href: SITE.social.instagram, testid: "footer-instagram-link" },
    { label: "WhatsApp", href: `https://wa.me/${wa}`, testid: "footer-whatsapp-link" },
  ].filter(Boolean);

  return (
    <footer className="noise-dark border-t border-zinc-800 text-zinc-400">
      <div className="container-x grid items-start gap-12 py-16 md:grid-cols-2 md:py-20 lg:grid-cols-12">
        {/* 1 — Brand */}
        <div className="lg:col-span-4">
          <div className="flex h-11 items-center">
            <Wordmark className="h-9" />
          </div>
          <p className="mt-5 max-w-xs text-sm leading-relaxed">
            Specialist commercial duct, exhaust and hygiene-systems cleaning for operating facilities across India.
          </p>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-dcc">{SITE.brandStatement}</p>
          <ul className="mt-6 flex flex-wrap gap-3">
            {socials.map((s) => (
              <li key={s.label}>
                <a href={s.href} target="_blank" rel="noopener noreferrer" data-testid={s.testid}
                  className="inline-flex min-h-[44px] items-center border border-zinc-700 px-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors hover:border-dcc hover:text-dcc">
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* 2 — Services */}
        <nav aria-label="Footer services" className="lg:col-span-3">
          <ColHead>Services</ColHead>
          <ul className="mt-5 space-y-3 text-sm leading-6">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <Link to={`/services/${s.slug}`} data-testid={`footer-service-${s.id}`} className="transition-colors hover:text-dcc">
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* 3 — Company */}
        <nav aria-label="Footer company" className="lg:col-span-2">
          <ColHead>Company</ColHead>
          <ul className="mt-5 space-y-3 text-sm leading-6">
            {COMPANY_NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} data-testid={`footer-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`} className="transition-colors hover:text-dcc">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* 4 — Contact */}
        <div className="lg:col-span-3">
          <ColHead>Contact</ColHead>
          <ul className="mt-5 space-y-3 text-sm leading-6">
            <li><a href={`tel:${phone}`} data-testid="footer-phone-link" className="transition-colors hover:text-dcc">{SITE.contact.phone}</a></li>
            <li><a href={`mailto:${SITE.contact.email}`} data-testid="footer-email-link" className="transition-colors hover:text-dcc">{SITE.contact.email}</a></li>
            <li>
              <a href={SITE.contact.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-address-link"
                className="group flex items-start gap-2 leading-relaxed transition-colors hover:text-dcc">
                <MapPin size={15} aria-hidden="true" className="mt-1 shrink-0 text-dcc" />
                <span>{SITE.contact.addressShort}</span>
              </a>
            </li>
            <li className="text-zinc-400">{SITE.contact.hours}</li>
          </ul>
          <Link to={SITE.cta.to} data-testid="footer-survey-cta" className="btn-ghost mt-7 text-xs !px-5 !py-3 text-zinc-200 hover:bg-dcc hover:border-dcc hover:text-white">
            {SITE.cta.label}
            <ArrowUpRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </div>

      {/* priority markets strip */}
      <nav aria-label="Priority locations" className="border-t border-zinc-800">
        <div className="container-x flex flex-wrap items-center gap-x-5 gap-y-2 py-5">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">Priority markets</span>
          {markets.map((m) => (
            <Link key={m.id} to={`/locations/${m.id}`} data-testid={`footer-location-${m.id}`}
              onClick={() => track("location_select", { location: m.id, placement: "footer" })}
              className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-dcc">
              {m.name}
            </Link>
          ))}
        </div>
      </nav>

      {/* bottom legal row */}
      <div className="border-t border-zinc-800">
        <div className="container-x flex flex-col gap-3 py-6 text-xs text-zinc-400 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} {SITE.name} All rights reserved.</p>
          {/* group ownership mark — same treatment as the profile site, links to Accordible */}
          <a
            href="https://www.accordible.in"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3"
            data-testid="footer-accordible"
            aria-label="Accordible — Integrated Facility Solutions (opens accordible.in)"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 transition-colors group-hover:text-zinc-300">An Accordible Company</span>
            <img
              src="/assets/accordible-logo.png"
              alt="Accordible — Integrated Facility Solutions"
              width="1047" height="150"
              loading="lazy" decoding="async"
              className="h-4 w-auto opacity-70 transition-opacity group-hover:opacity-100"
            />
          </a>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <li><Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-dcc">Privacy Policy</Link></li>
            <li><Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-dcc">Terms of Use</Link></li>
            <li><Link to="/standards" data-testid="footer-standards-link" className="transition-colors hover:text-dcc">Standards &amp; Frameworks</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
